import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { audiencesApi, useGetAudiencesForProjectQuery } from "../../redux/api/audiencesApi";
import { AUDIENCE_PAGE_SIZE, getAudienceDisplayName, resolveAudienceListBrandHash } from "../../constants/audiences";
import AudienceHeaderIcon from "./AudienceHeaderIcon";

export default function AudienceSwitcher({ currentHash, currentAudience }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const { currentProject } = useSelector((state) => state.project);
  const { currentBrand, brands, ready: brandsReady } = useSelector((state) => state.brand);
  const projectsLoaded = useSelector((state) => state.project.loaded);

  const queryArg = useMemo(() => {
    if (!currentProject?.hash || !projectsLoaded || !brandsReady) return null;
    const params = {
      project_hash: currentProject.hash,
      page: 1,
      page_size: AUDIENCE_PAGE_SIZE,
      sort_by: "fit_score",
    };
    const brandHash = resolveAudienceListBrandHash(currentProject, currentBrand, brands);
    if (brandHash) params.brand_hash = brandHash;
    return params;
  }, [brands, brandsReady, currentBrand, currentProject, projectsLoaded]);

  useGetAudiencesForProjectQuery(queryArg, { skip: !queryArg });

  const cachedAudiences = useSelector((state) => {
    if (!queryArg) return [];
    const queries = state[audiencesApi.reducerPath]?.queries ?? {};
    const byHash = new Map();
    for (const entry of Object.values(queries)) {
      const arg = entry?.originalArgs;
      if (
        arg?.project_hash !== queryArg.project_hash ||
        arg?.brand_hash !== queryArg.brand_hash ||
        !entry?.data?.results
      ) {
        continue;
      }
      for (const item of entry.data.results) {
        byHash.set(item.hash, item);
      }
    }
    return Array.from(byHash.values());
  });

  const audiences = useMemo(() => {
    const byHash = new Map(cachedAudiences.map((item) => [item.hash, item]));
    if (currentAudience?.hash) {
      byHash.set(currentAudience.hash, currentAudience);
    }
    return Array.from(byHash.values());
  }, [cachedAudiences, currentAudience]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const switchAudience = (hash) => {
    setOpen(false);
    if (hash === currentHash) return;
    const match = location.pathname.match(/^\/audience\/[^/]+(\/.*)?$/);
    const suffix = match?.[1] || "/";
    navigate(`/audience/${hash}${suffix}`);
  };

  const goToAllAudiences = () => {
    setOpen(false);
    navigate("/research/");
  };

  return (
    <div className="relative shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="p-1 rounded hover:bg-black hover:bg-opacity-10 transition-colors text-theme-text opacity-70 hover:opacity-100"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Switch audience"
      >
        <ChevronDownIcon className="h-5 w-5" />
      </button>
      {open ? (
        <div
          className="absolute left-0 top-full mt-1 w-72 max-h-80 flex flex-col bg-theme-secondary border border-theme-secondary rounded-lg shadow-lg z-50"
          role="listbox"
        >
          <div className="py-1 overflow-y-auto min-h-0">
            {audiences.map((item) => {
              const isCurrent = item.hash === currentHash;
              return (
                <button
                  key={item.hash}
                  type="button"
                  role="option"
                  aria-selected={isCurrent}
                  onClick={() => switchAudience(item.hash)}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                    isCurrent
                      ? "bg-black bg-opacity-10 text-theme-text"
                      : "text-theme-text hover:bg-black hover:bg-opacity-10"
                  }`}
                >
                  <span className="shrink-0 w-4 h-4 flex items-center justify-center leading-none">
                    <AudienceHeaderIcon audience={item} />
                  </span>
                  <span className="truncate">{getAudienceDisplayName(item)}</span>
                </button>
              );
            })}
          </div>
          <div className="border-t border-theme-secondary py-1 shrink-0">
            <button
              type="button"
              onClick={goToAllAudiences}
              className="w-full text-left px-4 py-2 text-sm text-theme-primary hover:bg-black hover:bg-opacity-10 transition-colors"
            >
              All Audiences
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
